import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RaadLogo } from '@/components/RaadLogo';
import { LangToggle } from '@/components/LangToggle';
import { WHATSAPP_URL, SUPPORT_EMAIL, SUPPORT_EMAIL_URL } from '@/lib/contact';

/**
 * Chrome shared by the two PUBLIC legal pages — `/privacy` and `/delete-account`
 * (slice 81). Both are reachable WITHOUT a session (Google Play requires the
 * privacy policy and the deletion page to be world-readable URLs), so they live
 * OUTSIDE `RequireAuth` in the router, exactly like `/t/:token`.
 *
 * Styled to match the sign-in screen: `bg-raad-bg`, the green brand mark
 * (unchanged — CLAUDE.md), 48px controls, a single readable column on a 360px
 * phone. Every string resolves through i18n; the EN/SO toggle is the same
 * `LangToggle` the login screen uses (pre-auth, it is a local-only choice).
 * NOTE: in SO mode only the delete-account title/body are Somali today (Mahad's
 * verbatim text); the chrome strings are English placeholders pending his Somali.
 */
export function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation();
  const brand = t('brand.name');

  return (
    // lg: html/body are overflow:hidden (styles.css, the map shell), so a
    // document route must scroll INSIDE itself — without this the policy was cut
    // at the viewport on a desktop screen (27 Oct 2026).
    <main className="flex min-h-dvh flex-col bg-raad-bg text-raad-text lg:h-dvh lg:overflow-y-auto">
      <header className="flex items-center justify-between gap-3 border-b border-raad-line-10 px-5 py-4 sm:px-8">
        <Link to="/login" className="flex items-center gap-3" aria-label={brand}>
          <RaadLogo size={36} title={`${brand} ${t('brand.suffix')}`} />
          <span className="font-display text-lg font-bold">
            {brand} <span className="text-raad-accent">{t('brand.suffix')}</span>
          </span>
        </Link>
        <LangToggle />
      </header>

      <div className="mx-auto w-full max-w-[720px] flex-1 px-5 py-8 sm:px-8 sm:py-10">
        <h1 className="font-display text-2xl font-bold sm:text-[28px]">{title}</h1>
        {children}

        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-12 flex-1 items-center justify-center rounded-xl bg-raad-primary px-4 text-sm font-bold text-raad-onprimary transition-opacity hover:opacity-95"
          >
            {t('legal.whatsapp')}
          </a>
          <a
            href={SUPPORT_EMAIL_URL}
            className="flex min-h-12 flex-1 items-center justify-center rounded-xl border border-raad-line-14 px-4 text-sm font-semibold text-raad-text transition-colors hover:border-raad-line-25"
          >
            {t('legal.email', { email: SUPPORT_EMAIL })}
          </a>
        </div>

        <p className="mt-8 text-center text-xs text-raad-dim">
          <Link
            to="/login"
            className="underline decoration-raad-line-20 underline-offset-2 hover:text-raad-text hover:decoration-raad-line-40"
          >
            {t('legal.backToSignIn')}
          </Link>
        </p>
      </div>
    </main>
  );
}
